import {
    Box,
    BoxProps,
    Circle,
    HStack,
    ScaleFade,
    useCheckbox,
    Text,
    UseCheckboxProps,
    useRadio,
    useRadioGroup,
    UseRadioProps
} from "@chakra-ui/react";
import {ReactNode} from "react"
import {CheckIcon} from "@chakra-ui/icons";

type CheckboxCardProps<T extends string> = {
    children: ReactNode
    value: T
    checkboxProps: UseCheckboxProps
};

type RadioCardProps <T extends string> = {
    children: ReactNode
    value: T;
    radioProps: UseRadioProps
};

type ChoiceProps = {
    choices: {value: string,title: string,desc?: string}[]
    currentChoice?: string
    onUpdate: (v: string) => void
    name?: string
    title: string
    subtitle?: string
};

export function RadioCard<T extends string>(props: RadioCardProps<T> & BoxProps) {
    const radio = useRadio(props.radioProps);
    return <Box
        as="label"
        cursor="pointer"
        position="relative"
        display="block"
        {...radio.htmlProps}
        _focusWithin={{
            "--clr": "var(--chakra-colors-primary-500)",
        }}
    >
        <input {...radio.getInputProps()} />
        <HStack
            align="center"
            justifyContent="space-between"
            gap=".5rem"
            {...radio.getRadioProps()}
            lineHeight="1.375"
            color="dark.500"
            p=".5rem"
            backgroundColor="#F5F7F9"
            _hover={{backgroundColor: "gray.100"}}
            css={{"--s-gap": ".25rem"}}
            border={props.border}
            borderRadius={props.borderRadius}
        >
            {props.children}
            <Check checked={radio.state.isChecked} type="radio"/>
        </HStack>
    </Box>;
}

export function CheckboxCard<T extends string>(props: CheckboxCardProps<T>) {
  const checkProps = useCheckbox(props.checkboxProps);
  return <Box
        as="label"
        cursor="pointer"
        position="relative"
        display="block"
        _focusWithin={{
            "--clr": "var(--chakra-colors-primary-500)",
        }}
    >
        <input {...checkProps.getInputProps()} />
        <HStack
            align="center"
            gap="1rem"
            justifyContent="space-between"
            {...checkProps.getCheckboxProps()}
            borderRadius="md"
            borderColor="grey.100"
            borderWidth="2px"
            lineHeight="1.375"
            color="dark.500"
            p=".75rem"
            backgroundColor="#F5F7F9"
            _hover={{backgroundColor: "gray.100"}}
            css={{"--s-gap": ".25rem"}}
            transition="all .35s ease"
        >
            {props.children}
            <Check  checked={checkProps.state.isChecked} type="checkbox"/>
      </HStack>
   </Box>;
}

function Check(props: {checked: boolean, type: "checkbox" | "radio"}) {
    return <Box
        bg={props.checked ? "#3dc485" : "white"}
        borderRadius={props.type === "checkbox" ? ".25rem" : "full"}
        w="20px"
        h="20px"
        display="flex"
        placeItems="center"
        justifyContent="center"
        borderColor={props.checked ? "transparent" : "grey.100" }
        borderWidth="2px"
        transition="all 0.2s ease-in-out"
        flexShrink="0"
        outline="2px solid var(--clr, transparent)"
        outlineOffset=".02em"
    >
        {props.checked && <ScaleFade in={props.checked} initialScale={0.7}>
            {
                props.type === "checkbox"
                ? <CheckIcon boxSize="12px" color="white"/>
                : <Circle bg="white" size="8px"/>
            }
        </ScaleFade>}
    </Box>
}

export function ChoiceRadio(p: ChoiceProps & BoxProps) {
    const {getRootProps, getRadioProps} = useRadioGroup({
        name: p.name ?? "choice-radio",
        defaultValue: p.currentChoice,
        onChange: (v) => p.onUpdate(v)
    });
    return <div className="stack">
        <Text fontWeight={p.fontWeight ?? "bold"} fontSize="1.1em" textTransform="capitalize">
            {p.title}
        </Text>
        {p.subtitle && <Text fontSize="0.9em" color="gray.500">
            {p.subtitle}
        </Text>}
        <HStack
            gap="1rem"
            align="stretch"
            justify={p.justifyContent ?? "space-between"}
            flexDir={p.flexDir}
            {...getRootProps()}
        >
            {p.choices.map((v) => <RadioCard
                key={v.value}
                value={v.value}
                radioProps={getRadioProps({value: v.value})}
                borderRadius="8px"
                border="2px solid var(--chakra-colors-gray-300)"
            >
                {
                    v.desc ?
                        <div className="stack">
                            <Text fontWeight="bold" textTransform="capitalize">
                                {v.title}
                            </Text>
                            <Text>{v.desc}</Text>
                        </div>
                        : <Text fontWeight="bold" textTransform="capitalize">
                            {v.title}
                        </Text>
                }
            </RadioCard>)}
        </HStack>
    </div>
}
